import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type IManagerUser } from '../../types/IManagerAuth';
import { sendEmailThunk, confirmCodeThunk } from './authThunks';

interface AuthState {
  isUserChecked: boolean;
  user: IManagerUser | null;
  isLoading: boolean;
  error: string | null;
  emailSent: boolean;
  isAuthenticated: boolean;
  // Реактивный признак того, что токены уже записаны в AuthService.
  // Нужен сокету: AuthService.isAuthorized — нереактивный геттер localStorage,
  // и на запись токенов React не ре-рендерит. Это поле выставляется ровно после
  // записи токенов (логин) и при старте App, поэтому подписка стартует без F5.
  tokensReady: boolean;
  currentEmail: string | null;
  rateLimitSeconds: number | null;
  rateLimitTimerId: NodeJS.Timeout | null;
  rateLimitEmail: string | null;
}

const initialState: AuthState = {
  isUserChecked: false,
  user: null,
  isLoading: false,
  error: null,
  emailSent: false,
  isAuthenticated: false,
  tokensReady: false,
  currentEmail: null,
  rateLimitSeconds: null,
  rateLimitTimerId: null,
  rateLimitEmail: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserChecked(state, action: PayloadAction<boolean>) {
      state.isUserChecked = action.payload;
    },
    setTokensReady(state, action: PayloadAction<boolean>) {
      state.tokensReady = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    resetAuthState(state) {
      state.emailSent = false;
      state.error = null;
      state.currentEmail = null;
      state.isLoading = false;
      // Очищаем таймер при сбросе состояния
      if (state.rateLimitTimerId !== null) {
        clearInterval(state.rateLimitTimerId);
        state.rateLimitTimerId = null;
      }
      state.rateLimitSeconds = null;
      state.rateLimitEmail = null;
    },
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.tokensReady = false;
      state.emailSent = false;
      state.currentEmail = null;
      state.error = null;
      // Очищаем таймер при выходе
      if (state.rateLimitTimerId !== null) {
        clearInterval(state.rateLimitTimerId);
        state.rateLimitTimerId = null;
      }
      state.rateLimitSeconds = null;
      state.rateLimitEmail = null;
    },
    startRateLimit(
      state,
      action: PayloadAction<{ seconds: number; email: string }>
    ) {
      state.rateLimitSeconds = action.payload.seconds;
      state.rateLimitEmail = action.payload.email;
    },
    decrementRateLimit(state) {
      if (state.rateLimitSeconds !== null && state.rateLimitSeconds > 0) {
        state.rateLimitSeconds -= 1;
        if (state.rateLimitSeconds === 0) {
          state.rateLimitSeconds = null;
          state.rateLimitEmail = null;
          if (state.rateLimitTimerId !== null) {
            clearInterval(state.rateLimitTimerId);
            state.rateLimitTimerId = null;
          }
        }
      }
    },
    setRateLimitTimer(state, action: PayloadAction<NodeJS.Timeout>) {
      state.rateLimitTimerId = action.payload;
    },
    clearRateLimit(state) {
      if (state.rateLimitTimerId !== null) {
        clearInterval(state.rateLimitTimerId);
        state.rateLimitTimerId = null;
      }
      state.rateLimitSeconds = null;
      state.rateLimitEmail = null;
    }
  },
  extraReducers: (builder) => {
    // Отправка email
    builder
      .addCase(sendEmailThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.currentEmail = action.meta.arg;
      })
      .addCase(sendEmailThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.emailSent = true;
        state.error = null;
      })
      .addCase(sendEmailThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload === undefined
            ? null
            : (action.payload?.message ?? 'Произошла ошибка при отправке кода');
        state.emailSent = false;
      });

    // Подтверждение кода
    builder
      .addCase(confirmCodeThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmCodeThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.emailSent = false;
        state.error = null;
        state.isUserChecked = true;
      })
      .addCase(confirmCodeThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload === undefined
            ? null
            : (action.payload?.message ??
              'Произошла ошибка при подтверждении кода');
      });
  }
});

export const {
  setUserChecked,
  setTokensReady,
  clearError,
  resetAuthState,
  logout,
  startRateLimit,
  decrementRateLimit,
  setRateLimitTimer,
  clearRateLimit
} = authSlice.actions;

export { sendEmailThunk, confirmCodeThunk } from './authThunks';

export default authSlice.reducer;
